import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { SERVICE_TYPES, BUDGETS, TIMELINES, COUNTRIES } from "@/lib/constants";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const STEPS = ["Need", "Business", "Details", "Budget", "Timeline", "Contact"];

export default function EnquiryForm() {
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [data, setData] = useState({
    service_type: "",
    business_name: "",
    industry: "",
    country: "",
    existing_website: "",
    project_details: "",
    budget: "",
    timeline: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    contact_company: "",
  });

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const canProceed = () => {
    if (step === 0) return !!data.service_type;
    if (step === 1) return data.business_name && data.industry && data.country;
    if (step === 2) return data.project_details.length >= 10;
    if (step === 3) return !!data.budget;
    if (step === 4) return !!data.timeline;
    if (step === 5) return data.contact_name && data.contact_email && data.contact_phone;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/leads", data);
      setDone(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="card-surface p-10 lg:p-14 text-center relative overflow-hidden"
        data-testid="enquiry-success"
      >
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-80 h-80 rounded-full glow-primary" />
        <div className="relative">
          <div className="mx-auto w-16 h-16 rounded-full grid place-items-center bg-brand-primary text-white mb-6">
            <Check size={26} />
          </div>
          <h3 className="font-serif text-4xl md:text-5xl tracking-tight">Thank you — <span className="italic">brief received</span>.</h3>
          <p className="mt-4 text-[15px] text-[color:var(--text-muted)] max-w-[520px] mx-auto">
            Our team will review your project and contact you within 24 hours.
          </p>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="card-surface p-6 md:p-10 lg:p-12 relative overflow-hidden" data-testid="enquiry-form">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-8">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center flex-1">
            <div className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-brand-primary" : "bg-[color:var(--border)]"}`} />
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mb-8">
        <div className="overline">Step {String(step + 1).padStart(2,'0')} / 06 · {STEPS[step]}</div>
        <div className="font-mono text-[11.5px] text-[color:var(--text-muted)]">Enquiry ID pending</div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.35 }}
        >
          {step === 0 && (
            <StepBlock title="What do you need?" subtitle="Pick the closest match — we'll dig into detail later.">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {SERVICE_TYPES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => set("service_type", s)}
                    className={`h-14 px-4 rounded-xl border text-left text-[13.5px] font-medium transition-colors ${
                      data.service_type === s
                        ? "border-brand-primary bg-brand-primary/10"
                        : "border-[color:var(--border-strong)] hover:bg-[color:var(--surface-elev)]"
                    }`}
                    data-testid={`enquiry-need-${s.toLowerCase().replace(/\s+/g,'-')}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </StepBlock>
          )}

          {step === 1 && (
            <StepBlock title="Tell us about your business.">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Business Name" value={data.business_name} onChange={(v) => set("business_name", v)} testId="enquiry-business-name" />
                <Field label="Industry" value={data.industry} onChange={(v) => set("industry", v)} testId="enquiry-industry" />
                <SelectField label="Country" value={data.country} onChange={(v) => set("country", v)} options={COUNTRIES} testId="enquiry-country" />
                <Field label="Existing Website (optional)" value={data.existing_website} onChange={(v) => set("existing_website", v)} testId="enquiry-existing-web" />
              </div>
            </StepBlock>
          )}

          {step === 2 && (
            <StepBlock title="Tell us about your project.">
              <textarea
                data-testid="enquiry-details"
                rows={7}
                value={data.project_details}
                onChange={(e) => set("project_details", e.target.value)}
                className="w-full rounded-xl border border-[color:var(--border-strong)] bg-transparent p-4 text-[14.5px] leading-relaxed focus:border-brand-primary transition-colors resize-none"
                placeholder="Goals, features, integrations, examples you admire…"
              />
              <div className="mt-2 text-[12px] text-[color:var(--text-muted)]">
                {data.project_details.length} characters — minimum 10.
              </div>
            </StepBlock>
          )}

          {step === 3 && (
            <StepBlock title="Estimated budget?" subtitle="Guides scope. You won't be held to it.">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {BUDGETS.map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => set("budget", b)}
                    className={`h-14 px-4 rounded-xl border text-[13.5px] font-medium transition-colors ${
                      data.budget === b ? "border-brand-primary bg-brand-primary/10" : "border-[color:var(--border-strong)] hover:bg-[color:var(--surface-elev)]"
                    }`}
                    data-testid={`enquiry-budget-${b.replace(/[£,\-–\s]/g,'')}`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </StepBlock>
          )}

          {step === 4 && (
            <StepBlock title="Timeline?" subtitle="When would you like to launch?">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {TIMELINES.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => set("timeline", t)}
                    className={`h-14 px-4 rounded-xl border text-[13.5px] font-medium transition-colors ${
                      data.timeline === t ? "border-brand-primary bg-brand-primary/10" : "border-[color:var(--border-strong)] hover:bg-[color:var(--surface-elev)]"
                    }`}
                    data-testid={`enquiry-timeline-${t.replace(/[\s\-–]/g,'').toLowerCase()}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </StepBlock>
          )}

          {step === 5 && (
            <StepBlock title="How can we reach you?">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Name" value={data.contact_name} onChange={(v) => set("contact_name", v)} testId="enquiry-contact-name" />
                <Field label="Email" value={data.contact_email} onChange={(v) => set("contact_email", v)} type="email" testId="enquiry-contact-email" />
                <Field label="Phone" value={data.contact_phone} onChange={(v) => set("contact_phone", v)} testId="enquiry-contact-phone" />
                <Field label="Company (optional)" value={data.contact_company} onChange={(v) => set("contact_company", v)} testId="enquiry-contact-company" />
              </div>
            </StepBlock>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Controls */}
      <div className="mt-10 flex items-center justify-between">
        <button
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="h-11 px-4 rounded-full text-[13px] font-medium inline-flex items-center gap-1.5 disabled:opacity-30 hover:bg-[color:var(--surface-elev)] transition-colors"
          data-testid="enquiry-back"
        >
          <ArrowLeft size={14} /> Back
        </button>
        {step < STEPS.length - 1 ? (
          <button
            onClick={() => canProceed() && setStep((s) => s + 1)}
            disabled={!canProceed()}
            className="h-11 px-5 rounded-full bg-[color:var(--text)] text-[color:var(--bg)] font-semibold text-[13px] inline-flex items-center gap-1.5 disabled:opacity-30 hover:opacity-90 transition-opacity"
            data-testid="enquiry-next"
          >
            Continue <ArrowRight size={14} />
          </button>
        ) : (
          <button
            onClick={submit}
            disabled={!canProceed() || submitting}
            className="h-11 px-6 rounded-full bg-brand-primary text-white font-semibold text-[13px] inline-flex items-center gap-2 disabled:opacity-40 hover:opacity-90 transition-opacity"
            data-testid="enquiry-submit"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <>Submit brief <ArrowRight size={14} /></>}
          </button>
        )}
      </div>
    </div>
  );
}

function StepBlock({ title, subtitle, children }) {
  return (
    <div>
      <h3 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">{title}</h3>
      {subtitle && <p className="text-[14.5px] text-[color:var(--text-muted)] mt-2">{subtitle}</p>}
      <div className="mt-8">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <label className="block">
      <span className="overline block mb-2">{label}</span>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 rounded-xl border border-[color:var(--border-strong)] bg-transparent px-4 text-[14.5px] focus:border-brand-primary transition-colors"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <span className="overline block mb-2">{label}</span>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full h-12 rounded-xl border border-[color:var(--border-strong)] bg-transparent px-4 text-left text-[14.5px] focus:border-brand-primary transition-colors flex items-center justify-between"
        data-testid={testId}
      >
        <span className={value ? "" : "text-[color:var(--text-muted)]"}>{value || "Select…"}</span>
        <ArrowRight size={14} className={`transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-20 top-full mt-2 w-full rounded-xl border border-[color:var(--border-strong)] bg-[color:var(--surface)] shadow-xl max-h-64 overflow-auto">
          {options.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => { onChange(o); setOpen(false); }}
              className="w-full text-left px-4 py-2.5 text-[13.5px] hover:bg-[color:var(--surface-elev)]"
              data-testid={`${testId}-opt-${o.toLowerCase().replace(/\s+/g,'-')}`}
            >
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
